import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Theme,
    Typography
} from "@mui/material";
import {
    FormikContextType,
    useFormikContext
} from './formik/Formik';
import MFSelectField from "./formik/SelectField";
import DocumentUpload from "./formik/DocumentUpload";
import { useState } from "react";
import {
    DeleteIcons,
    DialogCloseIcon
} from "../assets/SvgIcons";

const defaultDocumentTypeList = [
    {
        documentTypeLabel: "Order Approval Email",
        documentTypeValue: "Order Approval Email"
    },
    {
        documentTypeLabel: "Call Recordings mp3",
        documentTypeValue: "Call Recordings mp3"
    },
    {
        documentTypeLabel: "Payment Confirmation",
        documentTypeValue: "Payment Confirmation"
    },
    {
        documentTypeLabel: "Bill Receipt",
        documentTypeValue: "Bill Receipt"
    },
    {
        documentTypeLabel: "PPM/ Contribution Agreement",
        documentTypeValue: "PPM/ Contribution Agreement"
    },
    {
        documentTypeLabel: "Others",
        documentTypeValue: "Others"
    }
]

export default function TransactionsDocumentUpload({
    fileUploadFormats = ['image/png', 'image/jpeg', 'application/pdf', 'image/jpg', 'audio/mpeg', 'message/rfc822'],
    maxFileSize = "5 MB",
    documentTypeList = defaultDocumentTypeList,
    open,
    onClose
}: {
    fileUploadFormats?: Array<string>;
    maxFileSize?: string;
    documentTypeList?: Array<{ documentTypeLabel: string; documentTypeValue: string; }>;
    open: boolean;
    onClose: () => void;
}): JSX.Element {
    const {
        values,
        setFieldValue,
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const [isDocumentLoading, setDocumentLoading] = useState<boolean>(false)
    const [uploadedFileArray, setUploadedFileArray] = useState<Array<any>>([[]])

    const handleDocumentUploadStart = () => {
        setDocumentLoading(true)
    }

    const handleDocumentUploadEnd = () => {
        setDocumentLoading(false)
    }

    const handleDeleteDocument = (currentDocumentIndex: number) => {
        const modifiedDocuments = (values?.documents ?? []).filter((_: { documentId: string; documentType: string }, index: number) => index !== currentDocumentIndex)

        setUploadedFileArray(prevState => prevState.filter((_: any, index: number) => currentDocumentIndex !== index))

        setFieldValue("documents", modifiedDocuments)
    }

    const handleAddMoreDocument = () => {
        setUploadedFileArray(prevState => [...prevState, []])

        setFieldValue("documents", [
            ...(values?.documents ?? []),
            {
                documentId: "",
                documentType: ""
            }
        ])
    }

    const handleUploadedFile = (docIndex: number, file: any) => {
        setUploadedFileArray(prevState => prevState.map((data: any, index: number) => docIndex === index ? file : data))
    }

    return (
        <Dialog
            open={open}
            maxWidth='lg'
            sx={(theme: Theme) => ({
                '& .MuiPaper-root': {
                    borderRadius: '25px',
                    backgroundColor: theme.palette.common.white,
                    padding: "10px",
                    width: "1063px",
                    maxHeight: "80%"
                },
            })}
            keepMounted
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "10px"
                }}
            >
                <Box>
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Document Upload
                    </Typography>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: `${theme.palette.text.secondary}B2`,
                            marginTop: "10px"
                        })}
                    >
                        You can add minimum 1 attachment and maximum 5 attachments
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        padding: "5px",
                        "&:disabled": {
                            opacity: 0.5
                        }
                    }}
                    disabled={isDocumentLoading}
                    aria-label="close-transactions-document-upload-dialog"
                >
                    <DialogCloseIcon />
                </IconButton>
            </Box>
            <DialogContent
                sx={{
                    maxHeight: "100%",
                    overflowY: "auto",
                    padding: "10px 20px 10px 10px"
                }}
            >
                <Box
                    sx={{
                        marginTop: "25px",
                        display: "flex",
                        flexDirection: 'column',
                        rowGap: "25px",
                    }}
                >
                    {values?.documents?.map((
                        _: { documentId: string, documentType: string },
                        docIndex: number
                    ) => {
                        const previousSelectedDocumentTypes = values
                            ?.documents
                            ?.filter((_: { documentId: string, documentType: string }, index: number) => docIndex !== index)
                            ?.filter((obj: { documentId: string, documentType: string }) => !!obj?.documentType)
                            ?.map((obj: { documentId: string, documentType: string }) => obj?.documentType)
                            ?? [];

                        const modifiedDocumentTypeOptions = documentTypeList
                            ?.filter((obj: {
                                documentTypeLabel: string;
                                documentTypeValue: string;
                            }) => !previousSelectedDocumentTypes.includes(obj?.documentTypeValue))
                            ?? []

                        return (
                            <Box key={docIndex}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: 'column',
                                        rowGap: "25px"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            columnGap: "50px"
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <MFSelectField
                                                name={`documents[${docIndex}].documentType`}
                                                label="Select Document Type"
                                                items={documentTypeList.map((obj) => ({
                                                    key: obj.documentTypeLabel,
                                                    value: obj.documentTypeValue,
                                                }))}
                                                disabled={isDocumentLoading}
                                                sx={{
                                                    "&.MuiOutlinedInput-root": {
                                                        "& fieldset>legend": {
                                                            fontSize: "0.78em",
                                                        }
                                                    }
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    columnGap: "10px"
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        rowGap: "5px",
                                                        flex: 1
                                                    }}
                                                >
                                                    <DocumentUpload
                                                        name={`documents[${docIndex}].documentId`}
                                                        onFileUpload={handleDocumentUploadStart}
                                                        onFileUploaded={handleDocumentUploadEnd}
                                                        minFileSize=""
                                                        maxFileSize={maxFileSize}
                                                        fileUploadFormats={fileUploadFormats}
                                                        disabled={isDocumentLoading}
                                                        uploadedFile={uploadedFileArray?.[docIndex] ?? []}
                                                        setUploadedFile={(file: any) => handleUploadedFile(docIndex, file)}
                                                        isFileFormatRestrictionEnabled={false}
                                                    />
                                                </Box>
                                                {values?.documents?.length > 1 && (
                                                    <IconButton
                                                        sx={{
                                                            padding: "0px 5px 5px",
                                                            alignSelf: "center",
                                                            "&:hover": {
                                                                padding: "5px"
                                                            },
                                                            "&:disabled": {
                                                                opacity: 0.5
                                                            }
                                                        }}
                                                        onClick={() => handleDeleteDocument(docIndex)}
                                                        disabled={isDocumentLoading}
                                                    >
                                                        <DeleteIcons />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                                {(
                                    (values?.documents?.length - 1 === docIndex) &&
                                    values?.documents?.length < 5
                                ) && (
                                        <Button
                                            sx={(theme: Theme) => ({
                                                ...theme.typography.body_500,
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.primary[500],
                                                backgroundColor: theme.palette.common.white,
                                                border: `1px solid ${theme.palette.primary[500]}`,
                                                borderRadius: "10px",
                                                padding: "15px 40px",
                                                marginTop: "35px",
                                                "&:hover": {
                                                    background: theme.palette.primary[50]
                                                },
                                                "&:disabled": {
                                                    borderColor: theme.palette.text.disabled,
                                                }
                                            })}
                                            onClick={handleAddMoreDocument}
                                            disabled={isDocumentLoading}
                                        >
                                            + Add More
                                        </Button>
                                    )}
                            </Box>
                        )
                    })}
                </Box>
            </DialogContent>
            <DialogActions
                sx={{ justifyContent: "center" }}
            >
                <Button
                    variant="contained"
                    sx={(theme: Theme) => ({
                        ...theme.typography.body_600,
                        padding: "10px",
                        width: "50%"
                    })}
                    disabled={isDocumentLoading}
                    onClick={onClose}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog >
    )
}